import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Info,
  Check,
} from 'lucide-react';
import metricsApi, {
  Metric,
  CreateMetricRequest,
  UpdateMetricRequest,
  Cluster,
  SubDimension,
  METRIC_UNITS,
  METRIC_TYPES,
  AGGREGATION_METHODS,
  UPDATE_FREQUENCIES,
  IMPACT_DIRECTIONS,
} from '../../api/metricsApi';

interface MetricFormWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  metric?: Metric | null;
  clusters: Cluster[];
  subDimensions: SubDimension[];
}

const steps = ['Basic Information', 'Search Configuration', 'Review'];

export const MetricFormWizard: React.FC<MetricFormWizardProps> = ({
  open,
  onClose,
  onSuccess,
  metric,
  clusters,
  subDimensions,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CreateMetricRequest>({
    cluster: '',
    subDimensionId: '',
    name: '',
    searchName: '',
    definition: '',
    unit: 'percentage',
    metricType: 'score',
    isAggregatable: true,
    impactDirection: 'positive',
    aggregationMethod: 'sum',
    updateFrequency: 'annual',
    searchConfig: {
      tier1Queries: [''],
      tier2Queries: [''],
      tier3Queries: [''],
    },
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [filteredSubDimensions, setFilteredSubDimensions] = useState<SubDimension[]>([]);

  useEffect(() => {
    if (metric) {
      // Map the search configuration properly
      let searchConfig = {
        tier1Queries: [''],
        tier2Queries: [''],
        tier3Queries: [''],
      };
      
      if (metric.searchConfig) {
        const config = metric.searchConfig as any;
        const searchData = config.search_strategy || config;
        
        if (searchData.tier1_queries && searchData.tier1_queries.length > 0) {
          searchConfig.tier1Queries = searchData.tier1_queries;
        }
        if (searchData.tier2_queries && searchData.tier2_queries.length > 0) {
          searchConfig.tier2Queries = searchData.tier2_queries;
        }
        if (searchData.tier3_queries && searchData.tier3_queries.length > 0) {
          searchConfig.tier3Queries = searchData.tier3_queries;
        }
      }
      
      setFormData({
        cluster: metric.cluster?.code || '',
        subDimensionId: metric.subDimension?.id?.toString() || '',
        name: metric.name,
        searchName: metric.searchName || '',
        definition: metric.definition,
        unit: metric.unit || '',
        metricType: metric.metricType || 'score',
        isAggregatable: metric.isAggregatable,
        impactDirection: metric.impactDirection || 'positive',
        aggregationMethod: metric.aggregationMethod || 'sum',
        updateFrequency: metric.updateFrequency || 'annual',
        searchConfig: searchConfig,
      });
    }
  }, [metric]);

  useEffect(() => {
    if (formData.cluster) {
      const cluster = clusters.find(c => c.code === formData.cluster);
      if (cluster) {
        const filtered = subDimensions.filter(sd => sd.clusterId === cluster.id);
        setFilteredSubDimensions(filtered);
      }
    } else {
      setFilteredSubDimensions([]);
    }
  }, [formData.cluster, clusters, subDimensions]);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    if (!metric) {
      setFormData({
        cluster: '',
        subDimensionId: '',
        name: '',
        searchName: '',
        definition: '',
        unit: 'percentage',
        metricType: 'score',
        isAggregatable: true,
        impactDirection: 'positive',
        aggregationMethod: 'sum',
        updateFrequency: 'annual',
        searchConfig: {
          tier1Queries: [''],
          tier2Queries: [''],
          tier3Queries: [''],
        },
      });
    }
    setFormErrors({});
  };

  const validateStep = (step: number): boolean => {
    const errors: { [key: string]: string } = {};

    if (step === 0) {
      if (!formData.cluster) errors.cluster = 'Cluster is required';
      if (!formData.subDimensionId) errors.subDimensionId = 'Sub-dimension is required';
      if (!formData.name.trim()) errors.name = 'Display name is required';
      if (!formData.searchName.trim()) errors.searchName = 'Search name is required';
      if (!formData.definition.trim()) errors.definition = 'Definition is required';
      if (!formData.unit) errors.unit = 'Unit is required';
    } else if (step === 1) {
      const tier1Queries = formData.searchConfig?.tier1Queries || [];
      if (tier1Queries.length === 0 || tier1Queries.every(q => !q.trim())) {
        errors.tier1Queries = 'At least one Tier 1 query is required';
      }
      
      const allQueries = [
        ...(formData.searchConfig?.tier1Queries || []),
        ...(formData.searchConfig?.tier2Queries || []),
        ...(formData.searchConfig?.tier3Queries || []),
      ].filter(q => q.trim());
      
      const validationErrors = metricsApi.validateSearchQueries(allQueries);
      if (validationErrors.length > 0) {
        errors.queries = validationErrors.join('; ');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: '',
      });
    }
  };

  const handleQueryChange = (tier: 'tier1Queries' | 'tier2Queries' | 'tier3Queries', index: number, value: string) => {
    const newQueries = [...(formData.searchConfig?.[tier] || [])];
    newQueries[index] = value;
    
    setFormData({
      ...formData,
      searchConfig: {
        ...formData.searchConfig,
        [tier]: newQueries,
      },
    });

    if (formErrors.tier1Queries || formErrors.queries) {
      setFormErrors({
        ...formErrors,
        tier1Queries: '',
        queries: '',
      });
    }
  };

  const addQuery = (tier: 'tier1Queries' | 'tier2Queries' | 'tier3Queries') => {
    const currentQueries = formData.searchConfig?.[tier] || [];
    setFormData({
      ...formData,
      searchConfig: {
        ...formData.searchConfig,
        [tier]: [...currentQueries, ''],
      },
    });
  };

  const removeQuery = (tier: 'tier1Queries' | 'tier2Queries' | 'tier3Queries', index: number) => {
    const newQueries = [...(formData.searchConfig?.[tier] || [])];
    newQueries.splice(index, 1);
    
    setFormData({
      ...formData,
      searchConfig: {
        ...formData.searchConfig,
        [tier]: newQueries.length > 0 ? newQueries : [''],
      },
    });
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    try {
      const transformedData = {
        ...formData,
        searchConfig: {
          tier1_queries: formData.searchConfig?.tier1Queries?.filter(q => q.trim()) || [],
          tier2_queries: formData.searchConfig?.tier2Queries?.filter(q => q.trim()) || [],
          tier3_queries: formData.searchConfig?.tier3Queries?.filter(q => q.trim()) || [],
        }
      };
      
      if (metric) {
        const updateData: UpdateMetricRequest = transformedData as UpdateMetricRequest;
        await metricsApi.updateMetric(metric.metricId, updateData);
      } else {
        await metricsApi.createMetric(transformedData as CreateMetricRequest);
      }
      onSuccess();
      handleReset();
      onClose();
    } catch (err: any) {
      console.error('Error saving metric:', err);
      setFormErrors({
        submit: err.message || 'Failed to save metric. Please try again.',
      });
    }
  };

  const renderBasicInformation = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Cluster *
          </label>
          <select
            value={formData.cluster}
            onChange={(e) => handleInputChange('cluster', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
              formErrors.cluster ? 'border-red-500' : 'border-gray-300'
            }`}
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            <option value="">Select Cluster</option>
            {clusters.map((cluster) => (
              <option key={cluster.id} value={cluster.code}>
                {cluster.name}
              </option>
            ))}
          </select>
          {formErrors.cluster && (
            <p className="mt-1 text-sm text-red-600">{formErrors.cluster}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Sub-Dimension *
          </label>
          <select
            value={formData.subDimensionId}
            onChange={(e) => handleInputChange('subDimensionId', e.target.value)}
            disabled={!formData.cluster}
            className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
              formErrors.subDimensionId ? 'border-red-500' : 'border-gray-300'
            } ${!formData.cluster ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            <option value="">Select Sub-Dimension</option>
            {filteredSubDimensions.map((sd) => (
              <option key={sd.id} value={sd.id.toString()}>
                {sd.name}
              </option>
            ))}
          </select>
          {formErrors.subDimensionId && (
            <p className="mt-1 text-sm text-red-600">{formErrors.subDimensionId}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Display Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="The name shown in the UI"
            className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
              formErrors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          />
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Search Name *
          </label>
          <input
            type="text"
            value={formData.searchName}
            onChange={(e) => handleInputChange('searchName', e.target.value)}
            placeholder="The name used for searching/data collection"
            className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
              formErrors.searchName ? 'border-red-500' : 'border-gray-300'
            }`}
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          />
          {formErrors.searchName && (
            <p className="mt-1 text-sm text-red-600">{formErrors.searchName}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
          Definition *
        </label>
        <textarea
          value={formData.definition}
          onChange={(e) => handleInputChange('definition', e.target.value)}
          rows={3}
          className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
            formErrors.definition ? 'border-red-500' : 'border-gray-300'
          }`}
          style={{ fontFamily: '"Tenor Sans", sans-serif' }}
        />
        {formErrors.definition && (
          <p className="mt-1 text-sm text-red-600">{formErrors.definition}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Unit *
          </label>
          <select
            value={formData.unit}
            onChange={(e) => handleInputChange('unit', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            {METRIC_UNITS.map((unit) => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Update Frequency
          </label>
          <select
            value={formData.updateFrequency}
            onChange={(e) => handleInputChange('updateFrequency', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            {UPDATE_FREQUENCIES.map((freq) => (
              <option key={freq.value} value={freq.value}>
                {freq.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Metric Type
          </label>
          <select
            value={formData.metricType}
            onChange={(e) => handleInputChange('metricType', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            {METRIC_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Impact Direction
          </label>
          <select
            value={formData.impactDirection}
            onChange={(e) => handleInputChange('impactDirection', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            {IMPACT_DIRECTIONS.map((direction) => (
              <option key={direction.value} value={direction.value}>
                {direction.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Aggregation Method
          </label>
          <select
            value={formData.aggregationMethod}
            onChange={(e) => handleInputChange('aggregationMethod', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            {AGGREGATION_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Is Aggregatable
          </label>
          <div className="flex gap-4 mt-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="yes"
                checked={formData.isAggregatable}
                onChange={() => handleInputChange('isAggregatable', true)}
                className="form-radio text-[#FAAF90] focus:ring-[#FAAF90]"
              />
              <span className="ml-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="no"
                checked={!formData.isAggregatable}
                onChange={() => handleInputChange('isAggregatable', false)}
                className="form-radio text-[#FAAF90] focus:ring-[#FAAF90]"
              />
              <span className="ml-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>No</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSearchConfiguration = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-[#6B9BD1] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-gray-700 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
              Configure search queries for automated data collection. These queries will be used by the metric collection service to find relevant data.
            </p>
            <div className="text-xs text-gray-600 space-y-1" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
              <p className="font-semibold">Query Template Variables:</p>
              <p>• {'{country}'} - Country name</p>
              <p>• {'{year}'} - Target year</p>
              <p>• {'{country_domain}'} - Country-specific domain extension</p>
              <p>• {'{metric_name}'} - The search name of the metric</p>
              <p className="mt-2">
                <span className="font-semibold">Example:</span> (site:statistics.gov.{'{country_domain}'} OR site:oecd.org) AND "{'{metric_name}'}" AND {'{country}'} AND {'{year}'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {formErrors.queries && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-sm text-red-700">{formErrors.queries}</p>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
          Tier 1 Queries (Primary Search) *
        </h3>
        <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
          At least one query is required. If left empty, a default query will be generated using the search name.
        </p>
        {(formData.searchConfig?.tier1Queries || ['']).map((query, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange('tier1Queries', index, e.target.value)}
              placeholder="Enter a search query"
              className={`flex-1 px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] ${
                formErrors.tier1Queries ? 'border-red-500' : 'border-gray-300'
              }`}
              style={{ fontFamily: '"Tenor Sans", sans-serif' }}
            />
            {(formData.searchConfig?.tier1Queries?.length || 0) > 1 && (
              <button
                onClick={() => removeQuery('tier1Queries', index)}
                className="p-2 text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
        {formErrors.tier1Queries && (
          <p className="text-sm text-red-600 mb-2">{formErrors.tier1Queries}</p>
        )}
        <button
          onClick={() => addQuery('tier1Queries')}
          className="flex items-center gap-2 px-3 py-1 text-sm text-[#6B9BD1] hover:text-[#4A7FB8]"
          style={{ fontFamily: '"Tenor Sans", sans-serif' }}
        >
          <Plus className="w-4 h-4" />
          Add Query
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
          Tier 2 Queries (Secondary Search)
        </h3>
        {(formData.searchConfig?.tier2Queries || ['']).map((query, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange('tier2Queries', index, e.target.value)}
              placeholder="Enter a search query"
              className="flex-1 px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
              style={{ fontFamily: '"Tenor Sans", sans-serif' }}
            />
            {(formData.searchConfig?.tier2Queries?.length || 0) > 1 && (
              <button
                onClick={() => removeQuery('tier2Queries', index)}
                className="p-2 text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={() => addQuery('tier2Queries')}
          className="flex items-center gap-2 px-3 py-1 text-sm text-[#6B9BD1] hover:text-[#4A7FB8]"
          style={{ fontFamily: '"Tenor Sans", sans-serif' }}
        >
          <Plus className="w-4 h-4" />
          Add Query
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
          Tier 3 Queries (Tertiary Search)
        </h3>
        {(formData.searchConfig?.tier3Queries || ['']).map((query, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange('tier3Queries', index, e.target.value)}
              placeholder="Enter a search query"
              className="flex-1 px-3 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FAAF90] border-gray-300"
              style={{ fontFamily: '"Tenor Sans", sans-serif' }}
            />
            {(formData.searchConfig?.tier3Queries?.length || 0) > 1 && (
              <button
                onClick={() => removeQuery('tier3Queries', index)}
                className="p-2 text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={() => addQuery('tier3Queries')}
          className="flex items-center gap-2 px-3 py-1 text-sm text-[#6B9BD1] hover:text-[#4A7FB8]"
          style={{ fontFamily: '"Tenor Sans", sans-serif' }}
        >
          <Plus className="w-4 h-4" />
          Add Query
        </button>
      </div>
    </div>
  );

  const renderReview = () => {
    const cluster = clusters.find(c => c.code === formData.cluster);
    const subDimension = subDimensions.find(sd => sd.id.toString() === formData.subDimensionId);

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
          Review Metric Configuration
        </h3>
        
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[#FAAF90] mb-3" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Basic Information
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Cluster</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{cluster?.name || formData.cluster}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Sub-Dimension</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{subDimension?.name || 'Not selected'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Display Name</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{formData.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Search Name</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{formData.searchName}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Definition</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{formData.definition}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Unit</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                {METRIC_UNITS.find(u => u.value === formData.unit)?.label}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Metric Type</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                {METRIC_TYPES.find(t => t.value === formData.metricType)?.label}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Update Frequency</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                {UPDATE_FREQUENCIES.find(f => f.value === formData.updateFrequency)?.label}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Impact Direction</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                {IMPACT_DIRECTIONS.find(d => d.value === formData.impactDirection)?.label}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Aggregation Method</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
                {AGGREGATION_METHODS.find(m => m.value === formData.aggregationMethod)?.label}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Is Aggregatable</p>
              <p className="text-sm text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{formData.isAggregatable ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[#FAAF90] mb-3" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            Search Configuration
          </h4>
          
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Tier 1 Queries</p>
              <div className="flex flex-wrap gap-2">
                {formData.searchConfig?.tier1Queries?.filter(q => q.trim()).map((query, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                    style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                  >
                    {query}
                  </span>
                ))}
              </div>
            </div>

            {formData.searchConfig?.tier2Queries?.some(q => q.trim()) && (
              <div>
                <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Tier 2 Queries</p>
                <div className="flex flex-wrap gap-2">
                  {formData.searchConfig.tier2Queries.filter(q => q.trim()).map((query, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                      style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                    >
                      {query}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {formData.searchConfig?.tier3Queries?.some(q => q.trim()) && (
              <div>
                <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>Tier 3 Queries</p>
                <div className="flex flex-wrap gap-2">
                  {formData.searchConfig.tier3Queries.filter(q => q.trim()).map((query, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                      style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                    >
                      {query}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {formErrors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <p className="text-sm text-red-700">{formErrors.submit}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderBasicInformation();
      case 1:
        return renderSearchConfiguration();
      case 2:
        return renderReview();
      default:
        return 'Unknown step';
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>
            {metric ? 'Edit Metric' : 'Add New Metric'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    index < activeStep
                      ? 'bg-[#FAAF90] text-white'
                      : index === activeStep
                      ? 'bg-[#FAAF90] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index < activeStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-sm" style={{ fontFamily: '"Tenor Sans", sans-serif' }}>{index + 1}</span>
                  )}
                </div>
                <span
                  className={`ml-2 text-sm ${
                    index <= activeStep ? 'text-gray-900' : 'text-gray-500'
                  }`}
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                >
                  {step}
                </span>
                {index < steps.length - 1 && (
                  <div
                    className={`mx-4 h-0.5 w-20 ${
                      index < activeStep ? 'bg-[#FAAF90]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {getStepContent(activeStep)}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            style={{ fontFamily: '"Tenor Sans", sans-serif' }}
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {activeStep > 0 && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-[#6B9BD1] hover:text-[#4A7FB8] flex items-center gap-1"
                style={{ fontFamily: '"Tenor Sans", sans-serif' }}
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            {activeStep < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-[#FAAF90] text-white rounded-lg hover:bg-[#E59873] flex items-center gap-1"
                style={{ fontFamily: '"Tenor Sans", sans-serif' }}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#FAAF90] text-white rounded-lg hover:bg-[#E59873]"
                style={{ fontFamily: '"Tenor Sans", sans-serif' }}
              >
                {metric ? 'Update' : 'Create'} Metric
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};